import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import Icons from '@/components/Icons';
import { PasswordInput } from '@/components/ui/password-input';
import DsgvoConsentCheckbox from '@/components/DsgvoConsentCheckbox';
import { ArrowLeft, Mail, KeyRound } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { UserRole } from '@/types/model';
const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signIn,
    signUp,
    signInWithGoogle,
    signInWithMagicLink,
    isLoading,
    user
  } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'customer' | 'studio_owner'>('customer');
  const {
    toast
  } = useToast();
  const [dsgvoConsent, setDsgvoConsent] = useState(false);
  const [authMethod, setAuthMethod] = useState<'password' | 'magic-link'>('password');
  useEffect(() => {
    // Check if the path is /register or /login
    setIsSignUp(location.pathname === '/register');
  }, [location.pathname]);
  useEffect(() => {
    if (user) {
      // Get the intended destination from location state or default to dashboard
      const destination = location.state?.from || '/dashboard';
      navigate(destination, {
        replace: true
      });
    }
  }, [user, navigate, location.state]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSignUp && !dsgvoConsent) {
      toast({
        title: "Zustimmung erforderlich",
        description: "Bitte akzeptiere die Datenschutzbestimmungen und AGB, um fortzufahren.",
        variant: "destructive"
      });
      return;
    }
    if (!email) {
      toast({
        title: "Eingabe erforderlich",
        description: "Bitte gib deine E-Mail-Adresse ein.",
        variant: "destructive"
      });
      return;
    }
    if (authMethod === 'password' && !password) {
      toast({
        title: "Eingabe erforderlich",
        description: "Bitte gib dein Passwort ein.",
        variant: "destructive"
      });
      return;
    }
    if (isSignUp && password !== confirmPassword) {
      toast({
        title: "Passwörter stimmen nicht überein",
        description: "Bitte stelle sicher, dass deine Passwörter übereinstimmen.",
        variant: "destructive"
      });
      return;
    }

    // Validate password strength for signup
    if (isSignUp && authMethod === 'password') {
      const hasMinLength = password.length >= 8;
      const hasNumber = /\d/.test(password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      if (!hasMinLength || !hasNumber || !hasSpecialChar) {
        toast({
          title: "Passwort zu schwach",
          description: "Dein Passwort muss mindestens 8 Zeichen lang sein und mindestens eine Zahl sowie ein Sonderzeichen enthalten.",
          variant: "destructive"
        });
        return;
      }
    }
    try {
      if (isSignUp) {
        await signUp(email, password, role);
        toast({
          title: "Registrierung erfolgreich",
          description: "Du wurdest erfolgreich registriert. Bitte bestätige deine E-Mail-Adresse."
        });
      } else if (authMethod === 'magic-link') {
        await signInWithMagicLink(email);
        // Toast handled in the function
      } else {
        await signIn(email, password);
        toast({
          title: "Anmeldung erfolgreich",
          description: "Du hast dich erfolgreich angemeldet."
        });
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      // Toast handled in the respective functions
    }
  };
  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error: any) {
      // Toast handled in the function
    }
  };
  return <div className="flex items-center justify-center min-h-screen bg-chrona-background pb-16 pt-10">
      <div className="w-full max-w-md px-4">
        {isSignUp ?
      // Register View
      <>
            {location.pathname === '/register' && <div className="mb-6">
                <Button variant="ghost" className="text-chrona-gray-300 hover:text-chrona-text" onClick={() => navigate('/login')}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Zurück zum Login
                </Button>
              </div>}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-chrona-text">Konto erstellen</h1>
              <div className="mt-3">
                <p className="text-chrona-gray-300 text-lg">
                  Die einfachste Art, <br />
                  <span className="text-[#2C6E49]">Termine zu buchen</span>
                </p>
              </div>
            </div>

            <div className="mb-6">
              <Button type="button" variant="outline" className="w-full bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text hover:bg-chrona-gray-700 mb-2" onClick={handleGoogleSignIn} disabled={isLoading}>
                <Icons.google className="mr-2 h-5 w-5" />
                Mit Google registrieren
              </Button>
              <div className="relative mt-6 mb-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-chrona-gray-700"></span>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-chrona-background text-chrona-gray-300">oder mit E-Mail</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-chrona-gray-300">
                  Name
                </Label>
                <Input id="name" type="text" placeholder="Dein Name" value={name} onChange={e => setName(e.target.value)} className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-chrona-gray-300">
                  E-Mail
                </Label>
                <Input id="email" type="email" placeholder="deine@email.de" value={email} onChange={e => setEmail(e.target.value)} required className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text" />
              </div>
              
              {/* Role Selection */}
              <div className="space-y-3">
                <Label className="text-chrona-gray-300">
                  Registrieren als
                </Label>
                <RadioGroup defaultValue="customer" value={role} onValueChange={value => setRole(value as 'customer' | 'studio_owner')} className="flex flex-col space-y-2 mt-2">
                  <div className="flex items-center space-x-3 rounded-md border border-chrona-gray-700 p-3 bg-chrona-gray-800 hover:bg-chrona-gray-700 transition-colors cursor-pointer">
                    <RadioGroupItem value="customer" id="customer" className="text-chrona-accent" />
                    <Label htmlFor="customer" className="text-white font-medium cursor-pointer px-0 mx-[10px]">Kunde</Label>
                    <div className="ml-auto text-xs text-chrona-gray-400 mx-0">
                      Du möchtest Termine buchen
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 rounded-md border border-chrona-gray-700 p-3 bg-chrona-gray-800 hover:bg-chrona-gray-700 transition-colors cursor-pointer">
                    <RadioGroupItem value="studio_owner" id="studio_owner" className="text-chrona-accent" />
                    <Label htmlFor="studio_owner" className="text-white font-medium cursor-pointer py-0 px-0 mx-[11px]">Studio / Dienstleister</Label>
                    <div className="ml-auto text-xs text-chrona-gray-400 py-0 px-0 mx-[4px]">
                      Du bietest Termine an
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-chrona-gray-300">
                  Passwort
                </Label>
                <PasswordInput id="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text" />
                <p className="text-xs text-chrona-gray-400 mt-1">
                  Mindestens 8 Zeichen, eine Zahl und ein Sonderzeichen
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-chrona-gray-300">
                  Passwort bestätigen
                </Label>
                <PasswordInput id="confirm-password" placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text" />
              </div>
              <div className="mt-6">
                <DsgvoConsentCheckbox checked={dsgvoConsent} onCheckedChange={setDsgvoConsent} required={true} />
              </div>
              <Button type="submit" className="w-full bg-chrona-accent hover:bg-chrona-accent-light text-chrona-text" disabled={isLoading}>
                {isLoading ? <>
                    <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                    Bitte warten...
                  </> : 'Registrieren'}
              </Button>
              <div className="mt-4 text-center text-sm text-chrona-gray-300">
                Bereits registriert?{' '}
                <Link to="/login" className="text-chrona-accent hover:text-chrona-accent-light underline">
                  Anmelden
                </Link>
              </div>
            </form>
          </> :
      // Login View
      <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-chrona-text">Willkommen zurück</h1>
              <div className="mt-3">
                <p className="text-chrona-gray-300 text-lg">
                  Die einfachste Art, <br />
                  <span className="text-[#2C6E49]">Termine zu buchen</span>
                </p>
              </div>
            </div>

            <div className="mb-6">
              <Button type="button" variant="outline" className="w-full bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text hover:bg-chrona-gray-700 mb-2" onClick={handleGoogleSignIn} disabled={isLoading}>
                <Icons.google className="mr-2 h-5 w-5" />
                Mit Google anmelden
              </Button>
              <div className="relative mt-6 mb-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-chrona-gray-700"></span>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-chrona-background text-chrona-gray-300">oder mit E-Mail</span>
                </div>
              </div>
            </div>

            <Tabs defaultValue="password" onValueChange={value => setAuthMethod(value as 'password' | 'magic-link')}>
              <TabsList className="grid w-full grid-cols-2 bg-chrona-gray-800 mb-6">
                <TabsTrigger value="password" className="data-[state=active]:bg-chrona-gray-700">
                  <KeyRound className="h-4 w-4 mr-2" /> Passwort
                </TabsTrigger>
                <TabsTrigger value="magic-link" className="data-[state=active]:bg-chrona-gray-700">
                  <Mail className="h-4 w-4 mr-2" /> Magic Link
                </TabsTrigger>
              </TabsList>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-chrona-gray-300">
                    E-Mail
                  </Label>
                  <Input id="email" type="email" placeholder="deine@email.de" value={email} onChange={e => setEmail(e.target.value)} required className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text" />
                </div>
                
                <TabsContent value="password">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-chrona-gray-300">
                      Passwort
                    </Label>
                    <PasswordInput id="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text" />
                    <div className="flex justify-end">
                      <Link to="/forgot-password" className="text-xs text-chrona-accent hover:text-chrona-accent-light">
                        Passwort vergessen?
                      </Link>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="magic-link">
                  <div className="rounded-md bg-chrona-gray-800 border border-chrona-gray-700 p-4 mb-4">
                    <p className="text-sm text-chrona-gray-300">
                      Wir senden dir einen magischen Link an deine E-Mail-Adresse. 
                      Klicke darauf, um dich ohne Passwort einzuloggen.
                    </p>
                  </div>
                </TabsContent>
                
                <Button type="submit" className="w-full bg-chrona-accent hover:bg-chrona-accent-light text-chrona-text" disabled={isLoading}>
                  {isLoading ? <>
                      <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                      Bitte warten...
                    </> : authMethod === 'password' ? 'Anmelden' : 'Magic Link senden'}
                </Button>
              </form>
              
              <div className="mt-4 text-center text-sm text-chrona-gray-300">
                Noch kein Konto?{' '}
                <Link to="/register" className="text-chrona-accent hover:text-chrona-accent-light underline">
                  Registrieren
                </Link>
              </div>
            </Tabs>
          </>}
      </div>
    </div>;
};
export default Auth;